import { useEffect, useState } from 'react';
import { customerApi } from '../customer_api';

type Category = 'transactional' | 'reminders' | 'marketing';
type Channel = 'email' | 'sms' | 'whatsapp' | 'push';

interface Preference {
  category: Category;
  channel: Channel;
  allowed: boolean;
}

interface PreferencesResponse {
  preferences: Preference[];
  account_status: {
    sending_domain_status: string | null;
    twilio_status: string | null;
    name: string;
  };
}

const CATEGORIES: { id: Category; label: string; sub: string }[] = [
  {
    id: 'transactional',
    label: 'Transactional',
    sub: 'Booking confirmations, receipts, refund updates',
  },
  {
    id: 'reminders',
    label: 'Reminders',
    sub: 'Drop-off reminders, vaccine expiry, pack and credit expiry',
  },
  {
    id: 'marketing',
    label: 'Marketing',
    sub: 'Promotions, seasonal offers, newsletters',
  },
];

const CHANNELS: { id: Channel; label: string }[] = [
  { id: 'email', label: 'Email' },
  { id: 'sms', label: 'SMS' },
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'push', label: 'Push' },
];

const headingStyle: React.CSSProperties = {
  fontSize: '2.25rem',
  fontWeight: 500,
  letterSpacing: '-0.02em',
  margin: '0.4rem 0 1.5rem',
};

const cellStyle: React.CSSProperties = {
  padding: '0.85rem 1rem',
  borderBottom: '1px solid var(--border, oklch(0.915 0.005 80))',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

function Toggle({
  checked,
  disabled,
  onChange,
  ariaLabel,
}: {
  checked: boolean;
  disabled: boolean;
  onChange: (v: boolean) => void;
  ariaLabel: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      style={{
        width: '40px',
        height: '22px',
        borderRadius: '999px',
        border: '1px solid var(--border, oklch(0.915 0.005 80))',
        background: checked
          ? 'var(--primary, oklch(0.43 0.18 263))'
          : 'var(--surface, oklch(0.97 0.003 85))',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        position: 'relative',
        padding: 0,
        transition: 'background 120ms ease',
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: '2px',
          left: checked ? '20px' : '2px',
          width: '16px',
          height: '16px',
          borderRadius: '50%',
          background: 'white',
          boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
          transition: 'left 120ms ease',
        }}
      />
    </button>
  );
}

function LockedChip({ ariaLabel }: { ariaLabel: string }) {
  return (
    <span
      title="Required for service"
      aria-label={ariaLabel}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.35rem',
        padding: '0.2rem 0.55rem',
        borderRadius: '999px',
        fontSize: '0.7rem',
        background: 'var(--surface, oklch(0.97 0.003 85))',
        color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
        border: '1px solid var(--border, oklch(0.915 0.005 80))',
      }}
    >
      <svg
        width="10"
        height="12"
        viewBox="0 0 10 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path
          d="M2 5V3.5a3 3 0 016 0V5h.5A1.5 1.5 0 0110 6.5v4A1.5 1.5 0 018.5 12h-7A1.5 1.5 0 010 10.5v-4A1.5 1.5 0 011.5 5H2zm1 0h4V3.5a2 2 0 10-4 0V5z"
          fill="currentColor"
        />
      </svg>
      Required
    </span>
  );
}

export function NotificationPreferences() {
  const [data, setData] = useState<PreferencesResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);
  const [confirmStopAll, setConfirmStopAll] = useState(false);
  const [stopAllPending, setStopAllPending] = useState(false);
  const [stoppedBanner, setStoppedBanner] = useState(false);

  useEffect(() => {
    customerApi<PreferencesResponse>('/api/customer/notification-preferences')
      .then(setData)
      .catch(() => setLoadError('Could not load preferences. Please try again.'));
  }, []);

  // Auto-clear toast after 2.5s
  useEffect(() => {
    if (!toast) return;
    const handle = setTimeout(() => setToast(null), 2500);
    return () => clearTimeout(handle);
  }, [toast]);

  const setAllowed = async (category: Category, channel: Channel, next: boolean): Promise<void> => {
    if (!data) return;
    // Optimistic update
    const previous = data;
    const optimistic: PreferencesResponse = {
      ...data,
      preferences: data.preferences.map((p) =>
        p.category === category && p.channel === channel ? { ...p, allowed: next } : p,
      ),
    };
    setData(optimistic);
    try {
      await customerApi('/api/customer/notification-preferences', {
        method: 'PATCH',
        body: JSON.stringify({ category, channel, allowed: next }),
      });
      setToast({ kind: 'success', text: 'Preferences updated.' });
    } catch {
      setData(previous);
      setToast({ kind: 'error', text: 'Could not save change. Please try again.' });
    }
  };

  const stopAll = async (): Promise<void> => {
    setStopAllPending(true);
    try {
      // Use raw fetch — the endpoint returns 200 with an empty body, which
      // customerApi (which always calls res.json()) cannot parse.
      const res = await fetch('/api/customer/notification-preferences/all', {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`API ${res.status}`);
      // Reflect in-memory: every reminders+marketing row → false.
      if (data) {
        setData({
          ...data,
          preferences: data.preferences.map((p) =>
            p.category === 'transactional' ? p : { ...p, allowed: false },
          ),
        });
      }
      setStoppedBanner(true);
      setConfirmStopAll(false);
    } catch {
      setToast({ kind: 'error', text: 'Could not stop communications. Please try again.' });
    } finally {
      setStopAllPending(false);
    }
  };

  const lookup = (category: Category, channel: Channel): boolean => {
    if (category === 'transactional') return true;
    const row = data?.preferences.find((p) => p.category === category && p.channel === channel);
    return row?.allowed ?? false;
  };

  const emailUnconfigured =
    data !== null && data.account_status.sending_domain_status !== 'verified';
  const smsUnconfigured = data !== null && data.account_status.twilio_status !== 'verified';
  const whatsappUnconfigured = smsUnconfigured;

  const channelDisabled = (channel: Channel): boolean => {
    if (channel === 'email') return emailUnconfigured;
    if (channel === 'sms') return smsUnconfigured;
    if (channel === 'whatsapp') return whatsappUnconfigured;
    // push is never "server-unconfigured" — browser support is checked client-side
    // on the Profile/push-permission page; preferences are always editable here.
    return false;
  };

  return (
    <main className="min-h-[100dvh] bg-background px-4 py-6 sm:px-6 sm:py-10">
      <div className="mx-auto max-w-2xl">
        <h1 style={headingStyle}>Communication preferences</h1>

        {loadError && (
          <p style={{ color: 'var(--destructive, oklch(0.55 0.15 30))' }}>{loadError}</p>
        )}

        {!loadError && data === null && (
          <p style={{ color: 'var(--muted-foreground, oklch(0.52 0.008 75))' }}>Loading…</p>
        )}

        {data !== null && (
          <>
            {emailUnconfigured && (
              <output
                style={{
                  display: 'block',
                  padding: '0.75rem 1rem',
                  borderRadius: '8px',
                  background: 'var(--surface, oklch(0.97 0.003 85))',
                  border: '1px solid var(--border, oklch(0.915 0.005 80))',
                  margin: '0 0 0.75rem',
                  fontSize: '0.85rem',
                  color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
                }}
              >
                Email communication isn't currently configured by {data.account_status.name}. Reach
                out to them for updates.
              </output>
            )}
            {smsUnconfigured && (
              <output
                style={{
                  display: 'block',
                  padding: '0.75rem 1rem',
                  borderRadius: '8px',
                  background: 'var(--surface, oklch(0.97 0.003 85))',
                  border: '1px solid var(--border, oklch(0.915 0.005 80))',
                  margin: '0 0 0.75rem',
                  fontSize: '0.85rem',
                  color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
                }}
              >
                SMS and WhatsApp aren't currently configured by {data.account_status.name}. Reach
                out to them for updates.
              </output>
            )}
            {stoppedBanner && (
              <output
                style={{
                  display: 'block',
                  padding: '0.75rem 1rem',
                  borderRadius: '8px',
                  background: 'var(--surface, oklch(0.97 0.003 85))',
                  border: '1px solid var(--success, oklch(0.52 0.105 150))',
                  margin: '0 0 1rem',
                  fontSize: '0.85rem',
                  color: 'var(--foreground, oklch(0.19 0.012 75))',
                }}
              >
                All non-essential communication has been stopped. You'll still receive booking
                confirmations and receipts.
              </output>
            )}

            <table
              aria-label="Communication preferences"
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                border: '1px solid var(--border, oklch(0.915 0.005 80))',
                borderRadius: '12px',
                overflow: 'hidden',
                background: 'var(--surface, white)',
                marginBottom: '2rem',
                tableLayout: 'fixed',
              }}
            >
              <thead style={{ background: 'var(--surface-muted, oklch(0.97 0.003 85))' }}>
                <tr>
                  <th
                    scope="col"
                    style={{
                      ...cellStyle,
                      width: '40%',
                      textAlign: 'left',
                      fontSize: '0.72rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.12em',
                      color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
                      fontWeight: 500,
                    }}
                  >
                    Category
                  </th>
                  {CHANNELS.map((ch) => (
                    <th
                      key={ch.id}
                      scope="col"
                      style={{
                        ...cellStyle,
                        fontSize: '0.72rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.12em',
                        color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
                        fontWeight: 500,
                      }}
                    >
                      {ch.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {CATEGORIES.map((cat) => (
                  <tr key={cat.id}>
                    <th
                      scope="row"
                      style={{
                        ...cellStyle,
                        textAlign: 'left',
                        verticalAlign: 'top',
                        fontWeight: 500,
                      }}
                    >
                      <div>{cat.label}</div>
                      <div
                        style={{
                          fontSize: '0.78rem',
                          fontWeight: 400,
                          color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
                          marginTop: '0.2rem',
                        }}
                      >
                        {cat.sub}
                      </div>
                    </th>
                    {CHANNELS.map((ch) => (
                      <td key={ch.id} style={cellStyle}>
                        {cat.id === 'transactional' ? (
                          <LockedChip ariaLabel={`${cat.label} ${ch.label} required`} />
                        ) : (
                          <Toggle
                            checked={lookup(cat.id, ch.id)}
                            disabled={channelDisabled(ch.id)}
                            onChange={(next) => void setAllowed(cat.id, ch.id, next)}
                            ariaLabel={`${cat.label} ${ch.label}`}
                          />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>

            {toast && (
              <output
                style={{
                  display: 'block',
                  margin: '0 0 1rem',
                  fontSize: '0.85rem',
                  color:
                    toast.kind === 'success'
                      ? 'var(--success, oklch(0.52 0.105 150))'
                      : 'var(--destructive, oklch(0.55 0.15 30))',
                }}
              >
                {toast.text}
              </output>
            )}

            <div
              style={{
                borderTop: '1px solid var(--border, oklch(0.915 0.005 80))',
                paddingTop: '1.25rem',
              }}
            >
              {!confirmStopAll && (
                <button
                  type="button"
                  onClick={() => setConfirmStopAll(true)}
                  style={{
                    padding: '0.6rem 1.1rem',
                    borderRadius: '8px',
                    border: '1px solid var(--destructive, oklch(0.55 0.15 30))',
                    background: 'transparent',
                    color: 'var(--destructive, oklch(0.55 0.15 30))',
                    fontSize: '0.88rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                  }}
                >
                  Stop all communication
                </button>
              )}
              {confirmStopAll && (
                <section
                  aria-label="Confirm stop all communication"
                  aria-modal="true"
                  style={{
                    padding: '1rem 1.1rem',
                    borderRadius: '8px',
                    background: 'var(--surface, white)',
                    border: '1px solid var(--destructive, oklch(0.55 0.15 30))',
                  }}
                >
                  <p style={{ margin: '0 0 0.85rem', fontSize: '0.9rem' }}>
                    This stops all reminders and marketing on every channel. You'll still receive
                    essential booking communication. You can re-enable channels at any time.
                  </p>
                  <div style={{ display: 'flex', gap: '0.6rem' }}>
                    <button
                      type="button"
                      onClick={() => void stopAll()}
                      disabled={stopAllPending}
                      style={{
                        padding: '0.55rem 1rem',
                        borderRadius: '8px',
                        border: 'none',
                        background: 'var(--destructive, oklch(0.55 0.15 30))',
                        color: 'white',
                        fontSize: '0.85rem',
                        fontWeight: 500,
                        cursor: stopAllPending ? 'not-allowed' : 'pointer',
                        opacity: stopAllPending ? 0.7 : 1,
                      }}
                    >
                      {stopAllPending ? 'Stopping…' : 'Yes, stop all'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmStopAll(false)}
                      disabled={stopAllPending}
                      style={{
                        padding: '0.55rem 1rem',
                        borderRadius: '8px',
                        border: '1px solid var(--border, oklch(0.915 0.005 80))',
                        background: 'transparent',
                        color: 'var(--foreground, oklch(0.19 0.012 75))',
                        fontSize: '0.85rem',
                        fontWeight: 500,
                        cursor: stopAllPending ? 'not-allowed' : 'pointer',
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </section>
              )}
            </div>
          </>
        )}
      </div>
    </main>
  );
}
